/**
 * WhatsNewAugust2026Modal — Annonce des nouveautés (Phase 31)
 *
 * Modale one-shot affichée aux ÉLÈVES et ENSEIGNANTS connectés pour annoncer :
 *  - Le nouveau back-office (4 espaces distincts)
 *  - Le planning perso autonome + partage QR des bilans
 *  - La fiche moniteur publique avec QR de rattachement
 *  - Les fiches informelles côté élève
 *  - Le formulaire de contact admin interne
 *
 * Auto-dismiss : après 1 semaine (jusqu'au 6 août 2026 inclus) OU après un clic utilisateur.
 * Stockage : localStorage. Un compteur "vu N fois" évite d'agacer les utilisateurs qui l'ont juste fermé.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles, X, ArrowRight, QrCode, Calendar, Users, MessageCircle, ClipboardCheck,
  GraduationCap, Rocket,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';

const LS_KEY = 'c2r-whatsnew-aug2026-v1';
const EXPIRES_AT = new Date('2026-08-07T00:00:00Z'); // Ne plus afficher au-delà

export function WhatsNewAugust2026Modal() {
  const { user } = useApp() || {};
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    // Uniquement élèves et enseignants réels — PAS l'admin (qui connaît déjà les nouveautés)
    const role = user.role;
    if (role !== 'eleve' && role !== 'enseignant') return;
    // Ne plus afficher après la date d'expiration
    if (new Date() >= EXPIRES_AT) return;
    try {
      const seen = localStorage.getItem(LS_KEY);
      if (seen === '1') return;
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    } catch (_) { /* ignore */ }
  }, [user]);

  const close = () => {
    try { localStorage.setItem(LS_KEY, '1'); } catch (_) { /* ignore */ }
    setOpen(false);
  };

  const goTo = (path) => { close(); navigate(path); };

  if (!open || !user) return null;

  const isTeacher = user.role === 'enseignant';
  const isStudent = user.role === 'eleve';

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
      onClick={close}
      data-testid="whatsnew-aug2026-backdrop"
    >
      <div
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-gradient-to-b from-[#141416] to-[#0d0d0e] shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whatsnew-title"
        data-testid="whatsnew-aug2026-modal"
      >
        {/* Bandeau haut */}
        <div className="relative bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500 px-6 pt-6 pb-8">
          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/25 hover:bg-black/40 text-white/90 hover:text-white transition inline-flex items-center justify-center backdrop-blur"
            aria-label="Fermer"
            data-testid="whatsnew-close-top"
          >
            <X size={15} />
          </button>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/25 backdrop-blur text-white/90 text-[10.5px] font-bold uppercase tracking-widest mb-3">
            <Sparkles size={11} /> Nouveau · Août 2026
          </div>
          <h2
            id="whatsnew-title"
            className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {isTeacher ? 'Ton espace enseignant fait peau neuve' : 'Ton espace élève passe à la vitesse supérieure'}
          </h2>
          <p className="text-white/90 text-[13.5px] mt-1.5 leading-snug">
            {isTeacher
              ? 'Un back-office pensé pour toi, ton QR de rattachement, et zéro friction pour tes élèves.'
              : 'Ton planning perso, tes moniteurs, tes bilans partagés en un scan. Bienvenue dans la version 2026.'}
          </p>
        </div>

        {/* Contenu — liste des features */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <div className="space-y-3">
            {isTeacher ? (
              <>
                <FeatureLine
                  icon={QrCode}
                  color="rose"
                  title="Ta fiche moniteur avec QR"
                  desc="Un QR unique à imprimer / partager. Tes élèves te rejoignent en un scan."
                  cta="Ouvrir ma fiche"
                  onClick={() => goTo('/moniteur/ma-fiche')}
                  testid="whatsnew-cta-mafiche"
                />
                <FeatureLine
                  icon={Users}
                  color="emerald"
                  title="Crée un élève en 20 secondes"
                  desc="Plus besoin d'attendre le secrétariat — nom + formule, c'est parti."
                  cta="Voir mes élèves"
                  onClick={() => goTo('/moniteur/eleves')}
                  testid="whatsnew-cta-eleves"
                />
                <FeatureLine
                  icon={Calendar}
                  color="sky"
                  title="Ton planning autonome"
                  desc="Ajoute tes cours, partage le bilan à ton élève via un QR de fin de séance."
                  cta="Voir ma journée"
                  onClick={() => goTo('/moniteur/planning')}
                  testid="whatsnew-cta-planning"
                />
                <FeatureLine
                  icon={MessageCircle}
                  color="amber"
                  title="Un problème ? Un mot direct à l'admin"
                  desc="Bug, question, demande — envoie ta demande en 2 clics."
                  cta="Contacter l'admin"
                  onClick={() => goTo('/moniteur/contact-admin')}
                  testid="whatsnew-cta-contact"
                />
              </>
            ) : (
              <>
                <FeatureLine
                  icon={QrCode}
                  color="rose"
                  title="Rejoins ton moniteur en un scan"
                  desc="Ton moniteur a un QR ? Scanne-le, tu es rattaché en un clic."
                  cta="En savoir plus"
                  onClick={() => goTo('/eleve/mes-moniteurs')}
                  testid="whatsnew-cta-mes-moniteurs"
                />
                <FeatureLine
                  icon={Calendar}
                  color="emerald"
                  title="Ton planning perso"
                  desc="Ajoute tes cours, tes rendez-vous, tes révisions — tout au même endroit."
                  cta="Ouvrir mon planning"
                  onClick={() => goTo('/eleve/planning')}
                  testid="whatsnew-cta-planning"
                />
                <FeatureLine
                  icon={ClipboardCheck}
                  color="sky"
                  title="Tes bilans centralisés"
                  desc="Retrouve tous les bilans que ton moniteur t'a partagés au fil de tes leçons."
                  cta="Voir mes bilans"
                  onClick={() => goTo('/eleve/bilans')}
                  testid="whatsnew-cta-bilans"
                />
                <FeatureLine
                  icon={MessageCircle}
                  color="amber"
                  title="Une question ? On répond vite"
                  desc="Un souci sur le site, une idée à partager — écris-nous."
                  cta="Contacter l'admin"
                  onClick={() => goTo('/eleve/contact-admin')}
                  testid="whatsnew-cta-contact"
                />
              </>
            )}
          </div>

          {/* Pied de modale */}
          <div className="mt-5 pt-4 border-t border-white/[0.06] flex items-center justify-between gap-3">
            <div className="text-[11px] text-white/40 inline-flex items-center gap-1.5">
              <Rocket size={11} className="text-rose-400" />
              Cette annonce disparaîtra automatiquement le 7 août.
            </div>
            <button
              onClick={close}
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-white text-[12px] font-semibold transition"
              data-testid="whatsnew-close-btn"
            >
              C&apos;est noté <ArrowRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Ligne de feature ───────────────────────────────────────────────────
const COLORS = {
  rose: 'bg-rose-500/15 text-rose-300 border-rose-500/25',
  emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  sky: 'bg-sky-500/15 text-sky-300 border-sky-500/25',
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
};

function FeatureLine({ icon: Icon, color, title, desc, cta, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left group flex items-start gap-3 p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/15 transition"
    >
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${COLORS[color]}`}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-[14px] leading-tight">{title}</div>
        <div className="text-white/60 text-[12.5px] mt-0.5 leading-snug">{desc}</div>
        <div className="text-rose-300 text-[11.5px] font-semibold mt-1 inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
          {cta} <ArrowRight size={11} />
        </div>
      </div>
    </button>
  );
}

export default WhatsNewAugust2026Modal;
